import { Download, FileSpreadsheet } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useApi } from "../../lib/useApi";
import { openAttachment } from "../../lib/files";
import { Loader, ErrorState } from "../common/States";
import { Card, Kpi } from "../stats/StatsParts";
import { money } from "../../lib/billingLabels";

const Currency = ({ cur, v }) => (
  <div className="space-y-3" data-testid={`treasury-currency-${cur}`}>
    <p className="font-display text-lg font-semibold">{cur.toUpperCase()}</p>
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <Kpi testId={`tr-kpi-balance-${cur}`} label="Solde des comptes" value={money(v.balance, cur)} hint="mouvements validés" />
      <Kpi testId={`tr-kpi-invoiced-${cur}`} label="Facturé (TTC)" value={money(v.invoiced, cur)} hint={`HT : ${money(v.invoiced_ht, cur)}`} />
      <Kpi testId={`tr-kpi-recognized-${cur}`} label="Produits comptabilisés" value={money(v.recognized, cur)} hint="HT, prorata de la période de service" />
      <Kpi testId={`tr-kpi-collected-${cur}`} label="Encaissé" value={money(v.collected, cur)} hint="encaissements validés" />
      <Kpi testId={`tr-kpi-disbursed-${cur}`} label="Décaissé" value={money(v.disbursed, cur)} />
      <Kpi testId={`tr-kpi-net-${cur}`} label="Flux net" value={money(v.net_cash, cur)} />
      <Kpi testId={`tr-kpi-outstanding-${cur}`} label="Reste à encaisser" value={money(v.outstanding, cur)} />
      <Kpi testId={`tr-kpi-unpaid-${cur}`} label="Impayés échus" value={money(v.unpaid, cur)} />
    </div>
    {Object.keys(v.expenses_by_category).length > 0 && <p className="text-xs text-slate-600">Dépenses : {Object.entries(v.expenses_by_category).map(([k, a]) => `${k} ${money(a, cur)}`).join(" · ")}</p>}
  </div>
);

export default function TreasuryDashboard() {
  const [p, setP] = useState({ from: `${new Date().getFullYear()}-01-01`, to: new Date().toISOString().slice(0, 10) });
  const qs = `date_from=${p.from}&date_to=${p.to}`;
  const { data, loading, error, reload } = useApi(`/admin/treasury/dashboard?${qs}`);
  return (
    <div className="space-y-6" data-testid="treasury-dashboard">
      <div className="flex flex-wrap items-end gap-2">
        <div><p className="text-xs text-slate-500">Du</p><Input type="date" value={p.from} onChange={(e) => setP({ ...p, from: e.target.value })} data-testid="tr-period-from" /></div>
        <div><p className="text-xs text-slate-500">Au</p><Input type="date" value={p.to} onChange={(e) => setP({ ...p, to: e.target.value })} data-testid="tr-period-to" /></div>
        <Button variant="outline" onClick={() => openAttachment(`/admin/treasury/export?format=xlsx&${qs}`)} data-testid="tr-export-xlsx"><FileSpreadsheet className="mr-1 h-4 w-4" />Excel</Button>
        <Button variant="outline" onClick={() => openAttachment(`/admin/treasury/export?format=pdf&${qs}`)} data-testid="tr-export-pdf"><Download className="mr-1 h-4 w-4" />PDF</Button>
      </div>
      {loading && <Loader />}{error && <ErrorState message={error} onRetry={reload} />}
      {data && <>
        <p className="text-xs text-slate-500">Finances de l'éditeur uniquement. Totaux par devise, jamais additionnés. Une facture émise n'est jamais considérée comme encaissée. {data.pending_validation} mouvement(s) à valider · {data.unreconciled} encaissement(s) à rapprocher.</p>
        {!Object.keys(data.currencies).length && <p className="text-sm text-slate-500" data-testid="tr-dashboard-empty">Aucune donnée sur la période.</p>}
        {Object.entries(data.currencies).map(([c, v]) => <Currency key={c} cur={c} v={v} />)}
        {Object.entries(data.monthly).map(([c, rows]) => (
          <Card key={c} title={`12 derniers mois — ${c.toUpperCase()}`} testId={`tr-monthly-${c}`}>
            <div className="h-60"><ResponsiveContainer width="100%" height="100%"><BarChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} /><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis width={50} tick={{ fontSize: 10 }} /><Tooltip /><Legend />
              <Bar dataKey="invoiced" name="Facturé" fill="#6366F1" /><Bar dataKey="recognized" name="Produits" fill="#0EA5E9" /><Bar dataKey="collected" name="Encaissé" fill="#059669" /><Bar dataKey="disbursed" name="Décaissé" fill="#E11D48" />
            </BarChart></ResponsiveContainer></div>
          </Card>))}
      </>}
    </div>
  );
}
